import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	Renderer2,
	TemplateRef,
	ViewChild,
	computed,
	inject,
	signal,
	viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	ActivatedRoute,
	NavigationEnd,
	NavigationSkipped,
	Router,
	RouterLink,
	RouterModule,
} from '@angular/router';
import { IonMenu, IonNav, MenuController } from '@ionic/angular/standalone';
import { TranslocoModule } from '@ngneat/transloco';
import {
	ModulesEnum,
	UserRecordDto,
} from '@splitsecondconnect/ultramarine-shared';
import { filter, Subject, takeUntil } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { AppConfigService } from '../../../core/config/app/app.config.service';
import { UserRecordModel } from '../../../modules/admin/users/models/user-record.model';
import { ultraAnimations } from '../../../shared/animations';
import { UltraNavigationComponent } from '../../../shared/components/ultramarine-ui/ultra-navigation/ultra-navigation.component';
import { UltraToolTipDirective } from '../../../shared/directives/ulta-tooltip.directive';
import { mapUserDtoToModel } from '../../../shared/mapper/user.mapper';
import { BreakPointService } from '../../../shared/services/breakpoint.service';
import { MediaWatcherService } from '../../../shared/services/media-watcher.service';
import {
	ColorTheme,
	ThemeService,
} from '../../../shared/services/theme.service';

import { ModulesHeaderService } from './components/modules-header/modules-header.service';
import { UltraMenuCardComponent } from '../../../shared/components/ultramarine-ui/ultra-menu-card/ultra-menu-card.component';
import { NgComponentOutlet } from '@angular/common';
import { UltraNavigationItem } from '../../../shared/models/ultra-navigation-item.model';
import { PreferencesComponent } from '../../../modules/admin/preferences/preferences.component';
import { IonicModule } from '@ionic/angular';
import { UltraPrimaryButtonComponent } from '../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import { TerminalComponent } from 'app/shared/components/terminal/terminal.component';
import { UltraMenuItemWrapperComponent } from '../../../shared/components/ultramarine-ui/ultra-menu-item-wrapper/ultra-menu-item-wrapper.component';
import { UltraMenuItemComponent } from '../../../shared/components/ultramarine-ui/ultra-menu-item/ultra-menu-item.component';

@Component({
	selector: 'app-user-menu-template-wrapper',
	template: ` <ng-container *ngTemplateOutlet="template"></ng-container>`,
	imports: [NgTemplateOutlet],
	standalone: true,
})
class UserMenuTemplateWrapperComponent {
	@Input() template!: TemplateRef<any>;
}

@Component({
	selector: 'app-ultramarine-layout',
	templateUrl: './ultramarine-layout.component.html',
	styleUrls: ['ultramarine-layout.component.scss'],
	animations: ultraAnimations,
	imports: [
		UltraNavigationComponent,
		RouterModule,
		UltraToolTipDirective,
		RouterLink,
		UltraMenuCardComponent,
		NgComponentOutlet,
		IonicModule,
		UltraPrimaryButtonComponent,
		UltraMenuItemWrapperComponent,
		UltraMenuItemComponent,
		TerminalComponent,
		TranslocoModule,
	],
})
export class UltramarineLayout implements OnInit, OnDestroy {
	#config = inject(AppConfigService);
	localWindow = window as any;
	user = signal<UserRecordModel>(null);
	ionMenu = viewChild<IonMenu>('menu');
	renderer = inject(Renderer2);
	#appConfig = inject(AppConfigService);
	#auth = inject(AuthService);
	route = inject(ActivatedRoute);
	moduleMenu = viewChild<IonMenu>('moduleMenu');
	#menu = inject(MenuController);
	portalUrl = this.#config.config.portalUrl;
	userMenu = viewChild<IonMenu>('userMenu');
	terminalMenu = viewChild<IonMenu>('terminalMenu');
	router = inject(Router);
	userMenuNav = viewChild<IonNav>('userMenuNav');
	@ViewChild('userMenuRoot') userMenuRoot!: TemplateRef<any>;
	moduleEnum = ModulesEnum;

	private _unsubscribeAll: Subject<any> = new Subject<any>();
	isScreenSmall: boolean;
	showSideMenu: boolean = true;
	private _mediaWatcherService = inject(MediaWatcherService);
	operator = signal('');
	#modulesHeaderService = inject(ModulesHeaderService);
	document = inject(DOCUMENT);

	config = inject(AppConfigService);
	theme = inject(ThemeService);
	themeIcon = signal('dark_mode');
	fullScreen = signal(true);
	#breakpointService = inject(BreakPointService);
	isDesktop = toSignal(this.#breakpointService.isDesktop$);
	#authService = inject(AuthService);
	#router = inject(Router);
	activeRouteString = signal<string>('/');
	toolbarComponent = computed(() => {
		return this.#modulesHeaderService.toolbarComponent();
	});

	currentModule = computed(() => {
		return this.#modulesHeaderService.module();
	});

	link = computed(() => ['admin', this.currentModule(), 'new']);

	stats = computed(() => {
		return this.#modulesHeaderService.stats();
	});

	selectItems = computed(() => {
		return this.#modulesHeaderService.selectItems();
	});

	toPreferences() {
		this.userMenuNav().push(PreferencesComponent, {
			navReference: this.userMenuNav(),
		});
	}

	toggleIonMenu() {
		(document.activeElement as HTMLElement)?.blur();
		this.ionMenu().toggle();
	}

	closeIonMenu() {
		(document.activeElement as HTMLElement)?.blur();
		this.ionMenu().close();
	}

	closeModuleMenu() {
		this.moduleMenu()?.close();
	}

	closeUserMenu() {
		this.userMenu()?.close();
	}

	closeTerminal() {
		this.terminalMenu()?.close();
	}

	preferences: UltraNavigationItem = {
		id: 'preferences',
		title: 'preferences|title',
		type: 'basic',
		module: null,
	};

	ngOnInit(): void {
		this.#auth.user$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((res: UserRecordDto) => {
				this.user.set(mapUserDtoToModel(res));
			});

		this.#modulesHeaderService.openUuerMenu
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(() => {
				(document.activeElement as HTMLElement)?.blur();
				this.moduleMenu().open();
			});

		this.theme.theme$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((theme) => {
				this.themeIcon.set(
					theme === ColorTheme.DARK ? 'light_mode' : 'dark_mode',
				);
			});

		this.router.events
			.pipe(
				takeUntil(this._unsubscribeAll),
				filter(
					(event) =>
						event instanceof NavigationEnd ||
						event instanceof NavigationSkipped,
				),
			)
			.subscribe((res) => {
				(document.activeElement as HTMLElement)?.blur();
				this.activeRouteString.set(res.url + '/new');
				this.ionMenu()?.close();
				this.userMenu()?.close();
				this.moduleMenu()?.close();
				this.terminalMenu()?.close();
			});
	}

	ngAfterViewInit() {
		this.userMenuNav().setRoot(UserMenuTemplateWrapperComponent, {
			template: this.userMenuRoot,
		});
	}
	onUserMenuClosed() {
		this.userMenuNav().popToRoot();
	}

	openTerminalMenu() {
		this.terminalMenu().open();
	}

	navigedToModule = computed(() => {
		return this.activeRouteString().split('/').includes('admin');
	});

	openUserMenu() {
		(document.activeElement as HTMLElement)?.blur();
		this.userMenu().toggle();
	}

	toggleSideMenu() {
		this.showSideMenu = !this.showSideMenu;
	}

	logout() {
		this.#authService.navigateToLogout();
	}
	toPortal() {
		const portalUrl = this.#config.config.portalUrl;
		this.document.location.href = portalUrl;
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}
}
